// 調整レイヤーと塗りつぶしレイヤーのシェーダ。
// ADJUST: 下地 (uDst) を読み、種別 uKind に応じて色を変換した結果を出力する (α は保持)。
// LUT 系は 256×1 RGBA テクスチャ (R/G/B チャンネルごとの LUT、gradient-map は輝度→RGB)。
// 非 LUT 系は render/adjust/lut.ts の CPU 参照実装と同じ式。

export const ADJUST_KIND = {
  lut: 0,
  gradientMap: 1,
  threshold: 2,
  hueSaturation: 3,
  vibrance: 4,
  colorBalance: 5,
  blackWhite: 6,
  photoFilter: 7,
  channelMixer: 8,
  selectiveColor: 9,
  colorLookup: 10
} as const

export const ADJUST_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform sampler2D uDst;
uniform sampler2D uLut;      // 256x1 RGBA
uniform vec2 uDocSize;
uniform int uKind;
uniform vec4 uP0;            // 種別ごとのパラメータ
uniform vec4 uP1;
uniform vec4 uP2;
uniform vec4 uP3;
uniform float uSel[36];      // selective-color: 9 色域 × [c, m, y, k] (-1..1)
uniform sampler2D uLut3d;    // color-lookup: タイル化 3D LUT (幅 N*N, 高さ N)
uniform float uLutN;
out vec4 outColor;

float lum(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

vec3 rgb2hsl(vec3 c) {
  float mx = max(c.r, max(c.g, c.b));
  float mn = min(c.r, min(c.g, c.b));
  float l = (mx + mn) * 0.5;
  if (mx == mn) return vec3(0.0, 0.0, l);
  float d = mx - mn;
  float s = l > 0.5 ? d / (2.0 - mx - mn) : d / (mx + mn);
  float h;
  if (mx == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
  else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
  else h = (c.r - c.g) / d + 4.0;
  return vec3(h / 6.0, s, l);
}
float hueF(float p, float q, float t) {
  if (t < 0.0) t += 1.0;
  if (t > 1.0) t -= 1.0;
  if (t < 1.0 / 6.0) return p + (q - p) * 6.0 * t;
  if (t < 0.5) return q;
  if (t < 2.0 / 3.0) return p + (q - p) * (2.0 / 3.0 - t) * 6.0;
  return p;
}
vec3 hsl2rgb(vec3 hsl) {
  if (hsl.y == 0.0) return vec3(hsl.z);
  float q = hsl.z < 0.5 ? hsl.z * (1.0 + hsl.y) : hsl.z + hsl.y - hsl.z * hsl.y;
  float p = 2.0 * hsl.z - q;
  return vec3(hueF(p, q, hsl.x + 1.0 / 3.0), hueF(p, q, hsl.x), hueF(p, q, hsl.x - 1.0 / 3.0));
}
float lutAt(float v, int ch) {
  // テクセル中心をサンプル (256 段)
  float u = (floor(v * 255.0 + 0.5) + 0.5) / 256.0;
  vec4 t = texture(uLut, vec2(u, 0.5));
  return ch == 0 ? t.r : (ch == 1 ? t.g : t.b);
}

void main() {
  vec4 d = texture(uDst, vPos / uDocSize);
  vec3 c = d.rgb;
  vec3 o = c;
  if (uKind == 0) {
    o = vec3(lutAt(c.r, 0), lutAt(c.g, 1), lutAt(c.b, 2));
  } else if (uKind == 1) {
    float l = floor(lum(c) * 255.0 + 0.5) / 255.0;
    float u = (floor(l * 255.0 + 0.5) + 0.5) / 256.0;
    o = texture(uLut, vec2(u, 0.5)).rgb;
  } else if (uKind == 2) {
    float l = lum(c) * 255.0;
    o = vec3(l >= uP0.x ? 1.0 : 0.0);
  } else if (uKind == 3) {
    // hue-saturation: uP0 = (hue/360, sat/100, light/100, colorize), uP1 = (cHue/360, cSat/100, cLight/100, 0)
    vec3 hsl = rgb2hsl(c);
    if (uP0.w > 0.5) {
      float l = hsl.z;
      float nl = clamp(l + uP1.z * (uP1.z > 0.0 ? 1.0 - l : l), 0.0, 1.0);
      o = hsl2rgb(vec3(uP1.x, uP1.y, nl));
    } else {
      float nh = fract(hsl.x + uP0.x);
      float sk = uP0.y;
      float s = hsl.y;
      float ns = clamp(sk >= 0.0 ? s + (1.0 - s) * sk * (sk >= 1.0 ? 1.0 : sk) + s * sk * (1.0 - sk) : s * (1.0 + sk), 0.0, 1.0);
      float lk = uP0.z;
      float nl = clamp(lk >= 0.0 ? hsl.z + (1.0 - hsl.z) * lk : hsl.z * (1.0 + lk), 0.0, 1.0);
      o = hsl2rgb(vec3(nh, ns, nl));
    }
  } else if (uKind == 4) {
    // vibrance: uP0 = (vibrance/100, saturation/100)
    float mx = max(c.r, max(c.g, c.b));
    float mn = min(c.r, min(c.g, c.b));
    float sat = mx - mn;
    float avg = (c.r + c.g + c.b) / 3.0;
    float k = 1.0 + uP0.x * (1.0 - sat) + uP0.y;
    o = avg + (c - avg) * k;
  } else if (uKind == 5) {
    // color-balance: uP0 = shadows/100, uP1 = midtones/100, uP2 = highlights/100, uP3.x = preserveLum
    float l = lum(c);
    float ws = clamp(1.0 - l * 2.0, 0.0, 1.0);
    float wh = clamp(l * 2.0 - 1.0, 0.0, 1.0);
    float wm = 1.0 - ws - wh;
    o = c + (uP0.xyz * ws + uP1.xyz * wm + uP2.xyz * wh) * 0.5;
    if (uP3.x > 0.5) {
      float nl = lum(o);
      o += (l - nl);
    }
  } else if (uKind == 6) {
    // black-white: uP0 = (reds, yellows, greens, cyans)/100, uP1 = (blues, magentas, tint, 0), uP2 = tint rgb
    vec3 hsl = rgb2hsl(c);
    float l = lum(c);
    float bands[6];
    bands[0] = uP0.x; bands[1] = uP0.y; bands[2] = uP0.z; bands[3] = uP0.w; bands[4] = uP1.x; bands[5] = uP1.y;
    float hh = hsl.x * 6.0;
    int i0 = int(mod(floor(hh), 6.0));
    int i1 = int(mod(float(i0 + 1), 6.0));
    float f = hh - floor(hh);
    float w = bands[i0] * (1.0 - f) + bands[i1] * f;
    float gray = clamp(l + (w - 0.5) * hsl.y * 0.5, 0.0, 1.0);
    o = vec3(gray);
    if (uP1.z > 0.5) o = gray * uP2.xyz * 1.2;
  } else if (uKind == 7) {
    // photo-filter: uP0 = filter rgb, uP1 = (density, preserveLum)
    float dd = uP1.x;
    vec3 n = c * (1.0 - dd) + c * uP0.xyz * dd;
    if (uP1.y > 0.5) {
      float l = lum(c);
      float nl = lum(n);
      float k = nl > 0.0 ? l / nl : 1.0;
      n *= k;
    }
    o = n;
  } else if (uKind == 8) {
    // channel-mixer: uP0 = r row/100, uP1 = g row, uP2 = b row, uP3.x = monochrome
    float r = dot(uP0.xyz, c) + uP0.w;
    float g = dot(uP1.xyz, c) + uP1.w;
    float b = dot(uP2.xyz, c) + uP2.w;
    o = uP3.x > 0.5 ? vec3(r) : vec3(r, g, b);
  } else if (uKind == 9) {
    // selective-color: uP0.x = relative
    float mx = max(c.r, max(c.g, c.b)), mn = min(c.r, min(c.g, c.b));
    float wgt[9];
    wgt[0] = max(0.0, c.r - max(c.g, c.b));
    wgt[1] = max(0.0, min(c.r, c.g) - c.b);
    wgt[2] = max(0.0, c.g - max(c.r, c.b));
    wgt[3] = max(0.0, min(c.g, c.b) - c.r);
    wgt[4] = max(0.0, c.b - max(c.r, c.g));
    wgt[5] = max(0.0, min(c.r, c.b) - c.g);
    wgt[6] = max(0.0, (mn - 0.5) * 2.0);
    wgt[7] = max(0.0, 1.0 - (abs(2.0 * mx - 1.0) + abs(2.0 * mn - 1.0)) / 2.0);
    wgt[8] = max(0.0, (0.5 - mx) * 2.0);
    vec3 n = c;
    for (int i = 0; i < 9; i++) {
      float k = wgt[i];
      if (k <= 0.0) continue;
      float kk = uSel[i * 4 + 3];
      vec3 s = clamp(vec3(uSel[i * 4], uSel[i * 4 + 1], uSel[i * 4 + 2]) + kk, -1.0, 1.0);
      vec3 stepv;
      if (uP0.x > 0.5) stepv = vec3(s.x > 0.0 ? -s.x * c.r : -s.x * (1.0 - c.r), s.y > 0.0 ? -s.y * c.g : -s.y * (1.0 - c.g), s.z > 0.0 ? -s.z * c.b : -s.z * (1.0 - c.b));
      else stepv = -s;
      n += k * stepv;
    }
    o = n;
  } else if (uKind == 10) {
    // color-lookup: スライス z0/z1 をバイリニアで読んで線形補間 (三線形)
    if (uLutN >= 2.0) {
      float N = uLutN;
      float z = clamp(c.b, 0.0, 1.0) * (N - 1.0);
      float z0 = floor(z), z1 = min(N - 1.0, z0 + 1.0), fz = z - z0;
      vec2 xy = (clamp(c.rg, 0.0, 1.0) * (N - 1.0) + 0.5);
      vec2 uv0 = vec2((z0 * N + xy.x) / (N * N), xy.y / N);
      vec2 uv1 = vec2((z1 * N + xy.x) / (N * N), xy.y / N);
      o = mix(texture(uLut3d, uv0).rgb, texture(uLut3d, uv1).rgb, fz);
    }
  }
  outColor = vec4(clamp(o, 0.0, 1.0), d.a);
}
`

/** 塗りつぶしレイヤー: 単色 / グラデーション (文書全体) */
export const FILL_FRAG = /* glsl */ `#version 300 es
precision highp float;
in vec2 vPos;
uniform vec2 uDocSize;
uniform int uKind;           // 0 = solid, 1..5 = linear/radial/angle/reflected/diamond, 6 = pattern
uniform vec4 uColor;
uniform sampler2D uPattern;  // パターン (REPEAT)
uniform vec2 uPatSize;       // タイルの大きさ (文書 px、倍率込み)
uniform vec2 uPatOffset;
uniform sampler2D uStops;    // 256x1 RGBA (グラデーション LUT)
uniform vec2 uA;             // 始点 (文書座標)
uniform vec2 uB;             // 終点
out vec4 outColor;
void main() {
  if (uKind == 0) { outColor = uColor; return; }
  if (uKind == 6) { outColor = texture(uPattern, fract((vPos - uPatOffset) / uPatSize)); return; }
  vec2 d = uB - uA;
  float len2 = dot(d, d);
  vec2 p = vPos - uA;
  float t = 0.0;
  if (len2 > 0.0) {
    if (uKind == 1) t = dot(p, d) / len2;
    else if (uKind == 2) t = length(p) / sqrt(len2);
    else if (uKind == 3) { float base = atan(d.y, d.x); float a = atan(p.y, p.x) - base; a = mod(a, 6.2831853); t = a / 6.2831853; }
    else if (uKind == 4) t = abs(dot(p, d) / len2);
    else { float len = sqrt(len2); vec2 u = d / len; float along = abs(dot(p, u)); float perp = abs(-p.x * u.y + p.y * u.x); t = max(along, perp) / len; }
  }
  t = clamp(t, 0.0, 1.0);
  outColor = texture(uStops, vec2((t * 255.0 + 0.5) / 256.0, 0.5));
}
`
